import { createContext, memo, useContext, useMemo } from "react";
import { Box } from "../../primitives/box";
import { Text } from "../../primitives/text";
import type {
  BreadcrumbProps,
  BreadcrumbItemProps,
  BreadcrumbLinkProps,
} from "./types";
import { useBreadcrumbStyles } from "./styles";
import type { BreadcrumbStyles } from "./styles";

const BreadcrumbContext = createContext<BreadcrumbStyles | null>(null);

const useBreadcrumbContext = () => {
  const ctx = useContext(BreadcrumbContext);
  if (!ctx) {
    throw new Error(
      "BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator must be used inside Breadcrumb",
    );
  }
  return ctx;
};

export const Breadcrumb = memo(function Breadcrumb({
  children,
}: BreadcrumbProps) {
  const styles = useBreadcrumbStyles();

  return (
    <BreadcrumbContext.Provider value={styles}>
      <Box style={styles.container}>
        <ul style={styles.list}>{children}</ul>
      </Box>
    </BreadcrumbContext.Provider>
  );
});

export const BreadcrumbItem = memo(function BreadcrumbItem({
  children,
}: BreadcrumbItemProps) {
  const styles = useBreadcrumbContext();
  return <li style={styles.item}>{children}</li>;
});

export const BreadcrumbLink = memo(function BreadcrumbLink({
  children,
  href,
  onClick,
  isActive,
  color,
}: BreadcrumbLinkProps) {
  const styles = useBreadcrumbContext();

  const linkStyle = useMemo(
    () => ({
      ...styles.link,
      textDecoration: "none" as const,
      ...(isActive ? styles.active : {}),
      ...(color ? { color } : {}),
    }),
    [styles.link, styles.active, isActive, color],
  );

  if (!href && !onClick) {
    return (
      <Text as="span" style={linkStyle}>
        {children}
      </Text>
    );
  }

  return (
    <a href={href} onClick={onClick} style={linkStyle}>
      {children}
    </a>
  );
});

export const BreadcrumbSeparator = memo(function BreadcrumbSeparator() {
  const styles = useBreadcrumbContext();
  return <span style={styles.separator}>/</span>;
});
